import {
  isDockerEnvironment,
  isKubernetesEnvironment,
} from '@/react/portainer/environments/utils';
import {
  AppliedPolicy,
  AppliedPolicyStatus,
  AppliedPolicyStatusType,
  EnvironmentType,
} from '@/react/portainer/environments/types';

import { EnvironmentInfo, PolicyEnvironmentType, PolicyType } from '../types';

// isEnvironmentSupportedByPolicy checks if a given environment supports the specified policy type as per the response
// from the backend.
export function isEnvironmentSupportedByPolicy(
  environment: EnvironmentInfo,
  policyType: PolicyType
) {
  const policyStatus = getEnvironmentPolicyStatus(environment, policyType);

  return policyStatus.Status !== AppliedPolicyStatusType.NotSupported;
}

// getAppliedPolicyForType retrieves the applied policy object for a given policy type from an environment
export function getAppliedPolicyForType(
  environment: EnvironmentInfo,
  policyType: PolicyType
): AppliedPolicy | undefined {
  switch (policyType) {
    case 'rbac-docker':
      return environment.Policies?.dockerRBACPolicy;
    case 'rbac-k8s':
      return environment.Policies?.k8sRBACPolicy;
    case 'security-k8s':
      return environment.Policies?.k8sSecurityPolicy;
    case 'network-security-k8s':
      return environment.Policies?.k8sNetworkSecurityPolicy;
    case 'security-docker':
      return environment.Policies?.dockerSecurityPolicy;
    case 'setup-k8s':
      return environment.Policies?.k8sSetupPolicy;
    case 'setup-docker':
      return environment.Policies?.dockerSetupPolicy;
    case 'registry-k8s':
      return environment.Policies?.k8sRegistryPolicy;
    case 'registry-docker':
      return environment.Policies?.dockerRegistryPolicy;
    case 'change-confirmation':
      return environment.Policies?.changeConfirmationPolicy;
    case 'cleanup-docker':
      return environment.Policies?.dockerCleanupPolicy;
    case 'observability-k8s':
      return environment.Policies?.k8sObservabilityPolicy;
    case 'pod-security-standards-k8s':
      return environment.Policies?.k8sPodSecurityStandardsPolicy;
    case 'vulnerability-scanning-k8s':
      return environment.Policies?.k8sVulnerabilityScanningPolicy;
    case 'automatic-namespace-provisioning-k8s':
      return environment.Policies?.k8sAutomaticNamespaceProvisioningPolicy;
    default:
      return undefined;
  }
}

export function getEnvironmentPolicyStatus(
  environment: EnvironmentInfo,
  policyType: PolicyType
): AppliedPolicyStatus {
  const fallback = {
    Status: AppliedPolicyStatusType.NotSupported,
    Message: 'Not supported',
  };
  switch (policyType) {
    case 'rbac-docker':
      return environment.Policies?.dockerRBACPolicy?.policyStatus ?? fallback;
    case 'rbac-k8s':
      return environment.Policies?.k8sRBACPolicy?.policyStatus ?? fallback;
    case 'security-k8s':
      return environment.Policies?.k8sSecurityPolicy?.policyStatus ?? fallback;
    case 'network-security-k8s':
      return (
        environment.Policies?.k8sNetworkSecurityPolicy?.policyStatus ?? fallback
      );
    case 'security-docker':
      return (
        environment.Policies?.dockerSecurityPolicy?.policyStatus ?? fallback
      );
    case 'setup-k8s':
      return environment.Policies?.k8sSetupPolicy?.policyStatus ?? fallback;
    case 'setup-docker':
      return environment.Policies?.dockerSetupPolicy?.policyStatus ?? fallback;
    case 'registry-k8s':
      return environment.Policies?.k8sRegistryPolicy?.policyStatus ?? fallback;
    case 'registry-docker':
      return (
        environment.Policies?.dockerRegistryPolicy?.policyStatus ?? fallback
      );
    case 'change-confirmation':
      return (
        environment.Policies?.changeConfirmationPolicy?.policyStatus ?? fallback
      );
    case 'cleanup-docker':
      return (
        environment.Policies?.dockerCleanupPolicy?.policyStatus ?? fallback
      );
    case 'observability-k8s':
      return (
        environment.Policies?.k8sObservabilityPolicy?.policyStatus ?? fallback
      );
    case 'pod-security-standards-k8s':
      return (
        environment.Policies?.k8sPodSecurityStandardsPolicy?.policyStatus ??
        fallback
      );
    case 'vulnerability-scanning-k8s':
      return (
        environment.Policies?.k8sVulnerabilityScanningPolicy?.policyStatus ??
        fallback
      );
    case 'automatic-namespace-provisioning-k8s':
      return (
        environment.Policies?.k8sAutomaticNamespaceProvisioningPolicy
          ?.policyStatus ?? fallback
      );
    default:
      return fallback;
  }
}

// isEnvironmentTypeSupportedByPolicy checks if a given environment type is compatible with the specified policy environment type.
export function isEnvironmentTypeSupportedByPolicy(
  environmentType: EnvironmentType,
  policyRequiredType: PolicyEnvironmentType
) {
  if (policyRequiredType === 'kubernetes') {
    return isKubernetesEnvironment(environmentType);
  }
  if (
    policyRequiredType === 'docker' ||
    policyRequiredType === 'swarm' ||
    policyRequiredType === 'podman'
  ) {
    return isDockerEnvironment(environmentType);
  }

  return false;
}
